export type ClientType = "RESTAURANTE" | "SUPERMERCADO" | "COZINHA_INDUSTRIAL" | "CLIENTE_FINAL";
export type CommercialStatus = "PENDENTE_VALIDACAO" | "VALIDADO" | "REJEITADO" | "BLOQUEADO";
export type StaffRole = "ADMIN" | "ADMINISTRATIVO" | "VENDEDOR" | "PLANEJADOR" | "COMPRADOR" | "CONFERENTE" | "ENTREGADOR" | "PRODUCAO" | "EXPEDICAO" | "SEPARACAO";
export type AccountRole = "CLIENT" | StaffRole;
export type Role = AccountRole;

export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  ADMIN: "Administrador",
  ADMINISTRATIVO: "Administrativo",
  VENDEDOR: "Vendedor",
  PLANEJADOR: "Planejador",
  COMPRADOR: "Comprador",
  CONFERENTE: "Conferente",
  ENTREGADOR: "Entregador",
  PRODUCAO: "Produção",
  EXPEDICAO: "Expedição",
  SEPARACAO: "Separação IN NATURA",
};

/**
 * ADMINISTRATIVO herda todo o acesso de ADMIN, exceto gerenciar funcionários/cargos (ver
 * AdminFuncionarios.tsx) -- espelha AccountRole.isFullAdmin() no backend.
 */
export function isAdminEquivalent(role?: AccountRole): boolean {
  return role === "ADMIN" || role === "ADMINISTRATIVO";
}

export interface StaffMember {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  status: CommercialStatus;
  createdAt: string;
  commissionPercent?: number | null;
  /** Permissões efetivas do funcionário (códigos MODULO.SUBMODULO.ACAO). */
  permissions?: string[];
}
export type Category = string;
export type Unit = string;
export type CutType = string;

export type GlutenStatus = "NAO_CONTEM" | "CONTEM" | "PODE_CONTER";

/** Dados de etiqueta e informacao nutricional (por 100g) de um produto. */
export interface ProductLabelInfo {
  hasBarcode: boolean;
  ean?: string | null;
  netWeightGrams?: number | null;
  shelfLifeDays?: number | null;
  portionSizeGrams?: number | null;
  storageText?: string | null;
  glutenStatus: GlutenStatus;
  energyKcal?: number | null;
  carbsG?: number | null;
  sugarsTotalG?: number | null;
  sugarsAddedG?: number | null;
  proteinG?: number | null;
  fatTotalG?: number | null;
  fatSaturatedG?: number | null;
  fatTransG?: number | null;
  fiberG?: number | null;
  sodiumMg?: number | null;
}

export interface Product {
  id: string;
  name: string;
  category: Category;
  emoji?: string;
  imageUrl?: string;
  isProcessed?: boolean;
  defaultUnit: Unit;
  availableUnits?: Unit[];
  availableCuts?: CutType[];
  active?: boolean;
  displayOrder?: number;
  wastePercent?: number;
  priceByUnit?: Record<string, number>;
  /** Preços cadastrados por corte → unidadeId → preço (match exato tem prioridade e não recebe +10%). */
  priceByCutAndUnit?: Record<string, Record<string, number>>;
  pricePerUnit?: number;
  labelInfo?: ProductLabelInfo;
  /** Segmentos aos quais o produto pertence (linhas de mercado). */
  segments?: ClientType[];
  /**
   * Preços por célula (segmento, unidade, corte) definidos no cadastro. Transitório: enviado ao criar o
   * produto para popular as Tabelas Padrão dos segmentos; não faz parte da resposta de catálogo.
   */
  segmentPrices?: SegmentPriceInput[];
}

/** Célula de preço informada no cadastro: preço de um produto por segmento × unidade × corte. */
export interface SegmentPriceInput {
  segment: ClientType;
  unit: Unit;
  /** null/undefined = preço base (in natura / sem corte). */
  cut?: CutType | null;
  price: number;
}

export interface CatalogCategory {
  id: string;
  code: Category;
  label: string;
  emoji: string;
  sortOrder: number;
  operatorOnly: boolean;
  active: boolean;
}

export interface CatalogCutType {
  id: string;
  name: string;
  active: boolean;
}

export interface CatalogUnit {
  id: string;
  name: string;
  conversionToKg: number;
  active: boolean;
  /** Grupo de exibição no cadastro de produto (LITROS, BANDEJA, KG, PACOTE, UNIDADE). Ausente = "OUTRAS". */
  group?: string;
}

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  emoji: string;
  category: Category;
  cut: CutType;
  unit: Unit;
  quantity: number;
  deliveryWindow?: string;
  notes?: string;
  pricePerUnit?: number;
  /** Resolvido pelo backend: acréscimo de 10% por quantidade < 1 kg embutido no pricePerUnit. */
  smallQuantitySurcharge?: boolean;
}

export interface CompanyProfile {
  companyName: string;
  tradeName?: string | null;
  cnpj: string;
  phone: string;
  contactName: string;
  addressCep: string | null;
  addressLogradouro: string | null;
  addressNumero: string | null;
  addressComplemento: string | null;
  addressBairro: string | null;
  addressCidade: string | null;
  addressUf: string | null;
  clientType: ClientType;
}

export type OrderStatus = "CRIADO" | "EM_COMPRA" | "EM_PRODUCAO" | "EM_EXPEDICAO" | "EM_ROTA" | "ENTREGUE" | "CANCELADO";

/** Como o pedido sai da expedição: entrega por motorista ou retirada do cliente na fábrica. */
export type FulfillmentMode = "ENTREGA" | "RETIRADA";

export type ExpeditionItemStatus = "PENDENTE" | "CARREGADO" | "EM_FALTA";

export interface OrderExpeditionListItem {
  productId: string;
  productName: string;
  emoji?: string | null;
  imageUrl?: string | null;
  category?: string | null;
  unit: string;
  cut?: string | null;
  requestedQuantity: number;
  loadedQuantity?: number | null;
  isInNatura: boolean;
  status: ExpeditionItemStatus;
  reason?: string | null;
  expeditionUpdatedAt?: string | null;
  expeditionUpdatedBy?: string | null;
  /** Decisão da produção para o mesmo item — exibida na conferência de expedição. */
  productionStatus?: ProductionItemStatus | null;
  producedQuantity?: number | null;
}
export type OrderEventType =
  | "ORDER_CREATED"
  | "PURCHASE_SCHEDULED"
  | "PURCHASE_STARTED"
  | "PRODUCTION_DAY_SCHEDULED"
  | "PRODUCTION_DAY_UNSCHEDULED"
  | "PRODUCTION_AUTO_ADVANCED"
  | "PRODUCTION_SCHEDULED"
  | "PURCHASE_ITEM_UPDATED"
  | "PRODUCTION_ITEM_UPDATED"
  | "STATUS_CHANGED"
  | "DELIVERY_WINDOW_CHANGED"
  | "NOTES_CHANGED"
  | "ITEM_ADDED"
  | "ITEM_REMOVED"
  | "ITEM_UPDATED";

export interface OrderItem {
  id: string;
  externalId: string;
  productId: string;
  productName: string;
  emoji?: string;
  imageUrl?: string;
  category: Category;
  cut: CutType;
  unit: Unit;
  quantity: number;
  pricePerUnit?: number;
  wastePercent?: number;
  isProcessed?: boolean;
  cancelledAt?: string | null;
  /** Observação livre por item, definida no checkout/pedido manual/importação. */
  observation?: string | null;
}

export type ProductionItemStatus =
  | "PENDENTE"
  | "EM_ANDAMENTO"
  | "PRONTO"
  | "PARCIAL"
  | "SEM_MERCADORIA"
  | "CANCELADO";

export interface OrderProductionListItem {
  productId: string;
  productName: string;
  emoji?: string;
  imageUrl?: string;
  category: Category;
  unit: Unit;
  cut?: string | null;
  requestedQuantity: number;
  purchasedQuantity: number;
  productionQuantity: number;
  isProcessed: boolean;
  status: ProductionItemStatus;
  useStock?: boolean;
  producedQuantity?: number;
  reason?: string;
  productionUpdatedAt?: string;
  productionUpdatedBy?: string;
  /** Observação livre do cliente por item, agregada para a produção. */
  observation?: string | null;
}

export interface OrderPurchaseListItem {
  productId: string;
  productName: string;
  emoji?: string;
  imageUrl?: string;
  category: Category;
  unit: Unit;
  requestedQuantity: number;
  purchaseQuantity: number;
  wastePercent?: number;
  sourceItemIds: string[];
  purchased: boolean;
  useStock?: boolean;
  purchasedQuantity?: number;
  reason?: string;
  purchaseUpdatedAt?: string;
  purchaseUpdatedBy?: string;
}

export interface OrderHistoryEvent {
  type: OrderEventType;
  status?: OrderStatus;
  title: string;
  details?: string;
  actorEmail?: string;
  actorRole?: "CLIENT" | "ADMIN";
  at: string;
}

export interface OrderSummary {
  id: string;
  number?: number;
  clientId: string;
  clientName: string;
  clientType: ClientType;
  createdAt: string; // ISO
  deliveryWindow?: string;
  deliveryDate?: string; // ISO date (yyyy-MM-dd)
  status: OrderStatus;
  purchaseStartedAt?: string;
  productionScheduledAt?: string;
  /** Dia agendado (yyyy-MM-dd) para o pedido entrar em produção via scheduler. */
  scheduledProductionDate?: string;
  /** Avançou automaticamente para produção com itens de compra ainda pendentes. */
  productionAutoAdvancedPending?: boolean;
  expeditionScheduledAt?: string;
  expeditionReleasedAt?: string;
  routeStartedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  purchased: boolean;
  itemCount: number;
  itemPreview: string[];
  total?: number;
  lastUpdatedAt: string;
  priceTableId?: string;
  /** Entrega por motorista (padrão) ou retirada do cliente na fábrica. */
  fulfillmentMode?: FulfillmentMode;
}

export interface DeliveryConfirmation {
  id: string;
  orderId: string;
  nfNumber: string;
  driverName: string;
  driverEmail?: string;
  deliveredAt: string;
  truckTemperature: number;
  receiverName: string;
  receiverDocument: string;
  signatureUrl: string;
  receiptPhotoUrl: string;
}

export interface OrderDeliveryUnit {
  id: string;
  externalCode: string;
  name: string;
  address?: string | null;
  district?: string | null;
  city?: string | null;
  postalCode?: string | null;
  deliveryWindowText?: string | null;
  email?: string | null;
}

export interface OrderDetail extends OrderSummary {
  deliveryUnit?: OrderDeliveryUnit | null;
  items: OrderItem[];
  purchaseList: OrderPurchaseListItem[];
  productionList: OrderProductionListItem[];
  expeditionList: OrderExpeditionListItem[];
  notes?: string;
  history: OrderHistoryEvent[];
  historyTotal: number;
  procurementOrderId?: string;
  productionOrderId?: string;
  deliveryConfirmation?: DeliveryConfirmation | null;
  assignedDriverId?: string;
  assignedDriverName?: string;
}

export type Order = OrderDetail;

/** Taxonomia operacional da conta (espelha o backend). */
export type AccountKind = "TITULAR" | "UNIDADE" | "LEGADO_DIRETO";

export interface Account {
  /** Identificador ESTÁVEL da conta (UUID). Não muda (ao contrário do e-mail) — usado como chave de
   * preferências por usuário neste navegador. */
  id?: string;
  email: string;
  status: CommercialStatus;
  profile: CompanyProfile | null;
  /** Nome do funcionário do próprio cadastro (staff). Usado na saudação; null quando não cadastrado. */
  name?: string | null;
  role?: AccountRole;
  priceTableId?: string;
  /** Unidade vinculada quando a sessão é de conta de unidade independente; null/ausente = conta comum. */
  deliveryUnitId?: string | null;
  /** TITULAR = âncora comercial read-only (não compra); UNIDADE/LEGADO_DIRETO = operacional. */
  accountKind?: AccountKind;
  /** Permissões efetivas (códigos MODULO.SUBMODULO.ACAO) — autoridade de acesso a rotas/menus/botões. */
  permissions?: string[];
  /** Admin marcou a conta para trocar a senha no próximo acesso (troca autenticada, não concede acesso). */
  passwordResetRequired?: boolean;
  /** {@code true} quando a conta já tem senha local. Conta só-Google começa {@code false} (pode criar). */
  hasPassword?: boolean;
}

/**
 * {@code true} se a conta tem a permissão (código MODULO.SUBMODULO.ACAO).
 *
 * REGRA PERMANENTE: o cargo ADMIN sempre tem acesso a TUDO (inclusive permissões novas, automaticamente).
 * Espelha o gate de acesso do backend (`AuthorizationService.requirePermission`), que libera o ADMIN.
 */
export function hasPermission(account: Account | null | undefined, code: string): boolean {
  if (account?.role === "ADMIN") return true;
  return !!account?.permissions?.includes(code);
}

/** {@code true} se a conta tem ao menos uma das permissões informadas. */
export function hasAnyPermission(account: Account | null | undefined, ...codes: string[]): boolean {
  return codes.some((c) => hasPermission(account, c));
}

/** Conta titular: âncora comercial que NÃO opera compra (catálogo/carrinho/checkout bloqueados). */
export function isTitularAccount(account?: Account | null): boolean {
  return account?.accountKind === "TITULAR";
}

export function isStaffRole(role?: AccountRole): boolean {
  return !!role && role !== "CLIENT";
}

/**
 * Rotas de back-office em ordem de prioridade para o POUSO INICIAL, cada uma com a(s) permissão(ões)
 * que a liberam. O destino inicial de um funcionário é a PRIMEIRA rota cuja permissão ele possui — não
 * há destino fixo por cargo. Mantida alinhada com os gates de rota em App.tsx e com o menu (AdminLayout).
 */
export const STAFF_LANDING: { path: string; anyOf: string[] }[] = [
  { path: "/dashboard/operacoes", anyOf: ["PEDIDOS.PEDIDO.VISUALIZAR"] },
  { path: "/dashboard/compras", anyOf: ["PEDIDOS.COMPRA.AVANCAR"] },
  { path: "/dashboard/producao", anyOf: ["PEDIDOS.PRODUCAO.AVANCAR", "PEDIDOS.PRODUCAO.IN_NATURA"] },
  { path: "/dashboard/expedicao", anyOf: ["PEDIDOS.EXPEDICAO.AVANCAR"] },
  { path: "/admin/clientes", anyOf: ["CLIENTES.CLIENTE.VISUALIZAR"] },
  { path: "/admin/produtos", anyOf: ["CATALOGO.PRODUTO.VISUALIZAR"] },
  { path: "/admin/custos", anyOf: ["CATALOGO.CUSTO.VISUALIZAR"] },
  { path: "/admin/estoque", anyOf: ["SUPRIMENTOS.ESTOQUE.VISUALIZAR"] },
  { path: "/admin/entregas", anyOf: ["ENTREGAS.ROTA.VISUALIZAR"] },
  { path: "/admin/fornecedores", anyOf: ["SUPRIMENTOS.FORNECEDOR.VISUALIZAR"] },
  { path: "/admin/recebimentos", anyOf: ["SUPRIMENTOS.RECEBIMENTO.VISUALIZAR"] },
  { path: "/admin/nf-entrada", anyOf: ["FINANCEIRO.NF_ENTRADA.VISUALIZAR"] },
  { path: "/admin/relatorios/vendas", anyOf: ["RELATORIOS.VENDAS.VISUALIZAR"] },
  { path: "/admin/relatorios/produtos", anyOf: ["RELATORIOS.PRODUTOS.VISUALIZAR"] },
  { path: "/admin/relatorios/pedidos", anyOf: ["RELATORIOS.PEDIDOS.VISUALIZAR"] },
  { path: "/admin/relatorios/producao", anyOf: ["RELATORIOS.PRODUCAO.VISUALIZAR"] },
  { path: "/admin/relatorios/lista-de-compras", anyOf: ["RELATORIOS.LISTA_COMPRAS.VISUALIZAR"] },
  { path: "/admin/relatorios/precificacao", anyOf: ["RELATORIOS.PRECIFICACAO.VISUALIZAR"] },
  { path: "/admin/relatorios/recebimentos", anyOf: ["RELATORIOS.RECEBIMENTOS.VISUALIZAR"] },
  { path: "/admin/tabelas", anyOf: ["PRECOS.TABELA.VISUALIZAR"] },
  { path: "/admin/caixas", anyOf: ["CAIXAS.MOVIMENTACAO.VISUALIZAR"] },
  { path: "/admin/unidades-entrega", anyOf: ["CLIENTES.UNIDADE.VISUALIZAR"] },
  { path: "/admin/financeiro/contas-a-receber", anyOf: ["FINANCEIRO.CONTAS_RECEBER.VISUALIZAR"] },
  { path: "/admin/financeiro/resultados", anyOf: ["FINANCEIRO.RESULTADOS.VISUALIZAR"] },
  { path: "/admin/frota", anyOf: ["FROTA.VEICULO.VISUALIZAR"] },
  { path: "/admin/selecao", anyOf: ["SELECAO.PROCESSO.VISUALIZAR"] },
  { path: "/admin/funcionarios", anyOf: ["STAFF.FUNCIONARIO.VISUALIZAR"] },
  { path: "/admin/configuracoes/entregas", anyOf: ["ENTREGAS.CONFIG.VISUALIZAR"] },
];

/**
 * Single source of truth for "where should this account land after login / on /?".
 * ENTREGADOR uses the driver app at /entregador. Funcionários pousam na PRIMEIRA rota de back-office
 * cuja permissão possuem (sem destino fixo por cargo). Clients without a profile go through /onboarding.
 */
export function homeRouteFor(
  account:
    | { role?: AccountRole; status?: CommercialStatus; profile?: unknown; accountKind?: AccountKind; permissions?: string[] }
    | null
    | undefined,
  supplierNfEnabled = true,
): string {
  if (!account || !account.role) return "/login";
  if (account.role === "ENTREGADOR") return "/entregador";
  if (isStaffRole(account.role)) {
    const perms = account.permissions ?? [];
    // Quando o módulo NF está desligado, o pouso em /admin/nf-entrada é PULADO — senão um usuário que só
    // tem NF_ENTRADA cairia numa rota indisponível e o gate da rota o devolveria para cá, em loop.
    const landing = STAFF_LANDING.find((r) =>
      (supplierNfEnabled || r.path !== "/admin/nf-entrada") && r.anyOf.some((p) => perms.includes(p)));
    // Sem nenhuma permissão de back-office (ou só NF com módulo off): tela "sem acessos" (neutra).
    return landing ? landing.path : "/sem-acesso";
  }
  // Conta de unidade NÃO faz onboarding: opera sob o titular (o perfil comercial é do titular).
  // Clients without a saved profile always finish onboarding first, even if an
  // admin validated them in the meantime — otherwise AppLayout (redirects to
  // /onboarding when !profile) and Onboarding fight forever.
  if (!account.profile && account.accountKind !== "UNIDADE") return "/onboarding";
  return "/catalogo";
}

export function canManageOrders(role?: AccountRole): boolean {
  return isAdminEquivalent(role) || role === "PLANEJADOR" || role === "VENDEDOR";
}

export function canEditPricing(role?: AccountRole): boolean {
  return isAdminEquivalent(role);
}

// PRODUÇÃO and SEPARACAO don't see order values anywhere — cards, detail, totals.
/** Visibilidade de preço em pedidos agora é por PERMISSÃO (o backend também gateia por PEDIDOS.PRECOS.VISUALIZAR). */
export function canSeeOrderPricing(account?: Account | null): boolean {
  return hasPermission(account, "PEDIDOS.PRECOS.VISUALIZAR");
}

export interface AdminClientSummary {
  id: string;
  email: string;
  status: CommercialStatus;
  hasProfile?: boolean;
  companyName?: string;
  tradeName?: string | null;
  cnpj?: string | null;
  clientType?: ClientType;
  createdAt: string;
  vendorId?: string | null;
  vendorName?: string | null;
  commissionPercent?: number | null;
  effectiveCommissionPercent?: number | null;
  passwordResetRequired?: boolean;
  accountKind?: AccountKind;
}

export interface AdminClientDetail extends AdminClientSummary {
  id: string;
  email: string;
  role: "CLIENT" | "ADMIN";
  status: CommercialStatus;
  profile: CompanyProfile | null;
  createdAt: string;
  priceTableId?: string;
  vendorId?: string | null;
  vendorName?: string | null;
  commissionPercent?: number | null;
  effectiveCommissionPercent?: number | null;
  /** Se true (padrão), pedidos do cliente recebem o acréscimo de 10% para venda fracionada (< 1 kg). */
  smallQuantitySurchargeEnabled?: boolean;
  /** Condição financeira padrão (Financeiro/Contas a Receber). Congelada no título ao gerá-lo. */
  defaultPaymentTermDays?: number | null;
  defaultPaymentMethod?: "BOLETO" | "PIX" | "TRANSFERENCIA" | "DINHEIRO" | null;
  defaultInstallments?: number;
}

export type AdminClient = AdminClientDetail;

export interface PriceTableEntry {
  /** id da entrada já persistida; ausente em linhas novas ainda não salvas. */
  id?: string;
  productId: string;
  unitId: string;
  /** Corte ao qual o preço se aplica; null = preço base (sem corte). */
  cutTypeId?: string | null;
  cutName?: string | null;
  price: number;
  isOverride?: boolean;
}

/** Uma alteração REAL de preço no histórico (somente leitura): de quanto para quanto, quando e por quem. */
export interface PriceChange {
  oldPrice: number;
  newPrice: number;
  changedAt: string;
  actorEmail: string;
  actorRole: string;
}

export interface PriceTableChildSummary {
  id: string;
  name: string;
  markupPercent: number | null;
  stale: boolean;
}

export interface PriceTable {
  id: string;
  name: string;
  scope: ClientType;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  entryCount: number;
  linkedCustomerCount: number;
  /** Unidades de entrega que apontam para esta tabela via override (distinto do vínculo por cliente). */
  linkedDeliveryUnitCount?: number;
  parentId?: string | null;
  parentName?: string | null;
  markupPercent?: number | null;
  childCount?: number;
  stale?: boolean;
  /** TABELA PADRÃO BASE do segmento (resolvido pelo backend). Fixada e destacada no topo da lista. */
  defaultBase?: boolean;
}

export interface PriceTableLinkedCustomer {
  id: string;
  companyName?: string;
  email: string;
  status: CommercialStatus;
}

export interface PriceTableLinkedDeliveryUnit {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  active: boolean;
}

export interface PriceTableDetail extends PriceTable {
  entries: PriceTableEntry[];
  linkedCustomers: PriceTableLinkedCustomer[];
  linkedDeliveryUnits: PriceTableLinkedDeliveryUnit[];
  children: PriceTableChildSummary[];
}

// ── FASE 5: Fornecedores, Estoque, Recebimentos ──────────────────────────────

export interface Supplier {
  id: string;
  name: string;
  cnpj?: string | null;
  phone?: string | null;
  notes?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export type StockMovementType = "RECEIVING" | "PRODUCTION_CONSUMED" | "INVENTORY_ADJUSTMENT";

export interface StockBalance {
  productId: string;
  productName: string;
  categoryCode: string;
  unit: string;
  balance: number;
}

export interface StockMovement {
  id: string;
  movementType: StockMovementType;
  quantity: number;
  unit: string;
  notes?: string | null;
  createdAt: string;
  referenceId?: string | null;
}

export interface Receiving {
  id: string;
  supplierId: string;
  supplierName: string;
  receivedAt: string;
  invoiceNumber?: string | null;
  items: ReceivingItem[];
  registeredByEmail: string;
  registeredByName?: string | null;
  deliveryPersonName?: string | null;
  deliveryPersonDocument?: string | null;
  signatureUrl?: string | null;
  receiptPdfUrl?: string | null;
  createdAt: string;
  invoiceChangedAt?: string | null;
  invoiceChangedByEmail?: string | null;
  invoiceChangedByName?: string | null;
  invoiceChangeReason?: string | null;
}

export interface ReceivingItem {
  productId: string;
  productName: string;
  unit: string;
  quantity: number;
}

export interface AdminProductSummary {
  id: string;
  name: string;
  code?: number;
  category: Category;
  imageUrl?: string;
  defaultUnit: Unit;
  availableUnits: Unit[];
  availableCuts?: string[];
  active: boolean;
  isProcessed: boolean;
  wastePercent?: number;
  segments?: ClientType[];
}
